import Hero from "~/components/hero";
import { useTranslation } from "react-i18next";
import SectionChild from "~/components/SectionChild";
import BreadcrumbPageCus from "~/components/breadcrumb-page";
import type { Route } from "./+types/mua-ban-mua-ban-can-ho.index";
import { clientCallAPI } from "~/services/clientCallAPI";
import queryString from "query-string";
import { searchProduct } from "~/utils";
import { buildSeoMeta, getOrigin, buildMetaFromLoader } from '~/utils/seo';
import { Language } from "~/localization/resource";
import i18next from "~/localization/i18n.server";
import { IProduct } from "~/types/address";
import { CardRealEstate } from "~/components/card";
import { useLoaderData, useSearchParams } from 'react-router';
import Pagination from '~/components/pagination';

import { serverCallApiCMS } from '~/services/serverCallAPI';
import { IPage } from '~/types/types';
import { hostName } from '~/utils/format';
import { StructuredDataScript } from '~/components/structured-data-script';
import ContentPage from '../__components/content-page';
import { buildCanonical } from '~/utils/seo-common';
import { FAKE_PRODUCTS } from '~/data/fake-products';
import FormSearchProduct from './__components/form-search-product';
import FormContact from './__components/form-contact';

type ProductSearchResponse<T> = {
  data: T[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
};
export async function loader({ request }: Route.LoaderArgs) {
  const locale = ((await i18next.getLocale(request)) as Language) || "vi";

  const url = new URL(request.url);
  const origin = getOrigin(request);
  const searchParams = url.searchParams;
  const pageNumber = Number(searchParams.get("pageNumber")) || 1;
  const pageSize = Number(searchParams.get("pageSize")) || 9;
  const params = searchProduct({
    areaRange: searchParams.get("areaRange"),
    categoryId: searchParams.get("categoryId"),
    priceRange: searchParams.get("priceRange"),
    provinceId: searchParams.get("provinceId"),
    search: searchParams.get("search"),
  });
  const stringified = queryString.stringify(
    {
      ...params,
      pageNumber,
      pageSize,
    },
    { skipEmptyString: true, skipNull: true }
  );

  let dataProduct: ProductSearchResponse<IProduct> = await clientCallAPI(
    "/api/product?" + stringified,
    {
      headers: {
        "Accept-Language": locale,
      },
    }
  ).catch(() => {
    return {
      data: [],
      currentPage: pageNumber,
      totalPages: 1,
      pageSize,
      totalCount: 0,
    } satisfies ProductSearchResponse<IProduct>;
  });
  let isFake = false;
  // if (!dataProduct?.data?.length) {
  //   dataProduct = {
  //     data: FAKE_PRODUCTS,
  //     currentPage: 1,
  //     totalPages: 1,
  //     pageSize,
  //     totalCount: FAKE_PRODUCTS.length,
  //   };
  //   isFake = true;
  // }

  const contentPage = await serverCallApiCMS<IPage>(
    "/api/v1/web/pages/mua-ban-mua-ban-can-ho"
  ).catch(() => {
    return {
      slug: "",
      description: "",
      content: "",
      seo: {
        title: "",
        meta_description: "",
        focus_keyphrase: "",
        og_image: [],
      },
      structured: null,
      structuredData: null,
    };
  });
  const canonicalUrl = origin
    ? `${origin}${url.pathname}${url.search}`
    : `${url.pathname}${url.search}`;

  return {
    isFake,
    dataProduct,
    params,
    page: {
      pageNumber,
      pageSize,
    },
    seo: {
      canonical: canonicalUrl,
      ...contentPage.seo,
    },
    structuredData: contentPage.structuredData ?? null,
    contentPage,
  };
}



function ListProduct({ listProduct }: { listProduct: IProduct[] }) {
  return listProduct.length > 0 ? (
    <div className="grid grid-cols-1 gap-6 pb-16 md:grid-cols-3">
      {listProduct.map((product) => {
        return (
          <div
            className="w-full md:max-w-[240px]
                md:h-[340px]
                lg:max-w-full lg:min-h-[520px] lg:h-auto"
            key={product.id}
          >
            <CardRealEstate product={product} />
          </div>
        );
      })}
    </div>
  ) : (
    <div className="py-8 text-center">
      Hiện không có dự án nào phù hợp với từ khóa đã chọn.
    </div>
  );
}

export { action } from "./__consultation-form.server";
function Product() {
  const loaderData = useLoaderData<typeof loader>();
  const { t } = useTranslation("common");
  const { pageNumber, pageSize, } = loaderData.page;
  const [searchParams, setSearchParams] = useSearchParams();
  const handleSetSearchParams = (next: Record<string, string | number>) => {
    const current = Object.fromEntries(searchParams);
    const merged = {
      ...current,
      ...Object.entries(next).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>),
    };

    Object.keys(merged).forEach((key) => {
      if (
        merged[key] === '' ||
        merged[key] === 'undefined' ||
        merged[key] === 'null'
      ) {
        delete merged[key];
      }
    });
    setSearchParams(merged);
  };

  return (
    <div>
      <StructuredDataScript data={loaderData.structuredData} />
      <Hero image="/image/product.webp" title="Product" />
      <SectionChild>
        <FormSearchProduct params={loaderData.params} page={loaderData.page} />
        <BreadcrumbPageCus title={t("header.menu.products")} />
        <ListProduct listProduct={loaderData.dataProduct.data} />
        <Pagination
          currentPage={loaderData.dataProduct.currentPage || pageNumber}
          totalPages={loaderData.dataProduct.totalPages || 1}
          pageSize={loaderData.dataProduct.pageSize || pageSize}
          totalItems={loaderData.dataProduct.totalCount}
          onPageChange={(page) => handleSetSearchParams({ pageNumber: page })}
          onPageSizeChange={(size) =>
            handleSetSearchParams({ pageSize: size, pageNumber: 1 })
          }
        />
      </SectionChild>
      <SectionChild className="pb-[60px]">
        <FormContact />
      </SectionChild>
      <div className="pb-[100px]">
        <ContentPage content={loaderData.contentPage.content} />
      </div>
    </div>
  );
}

export default Product;



export const meta = (args: Route.MetaArgs) => {
  return buildMetaFromLoader(
    args,
    {
      hostName,
      buildSeoMeta,
    },
    (ld) => ld
  );
};

export const handle = {
  dynamicLinks: (props: any) => {
    const canonical = props?.data?.seo?.canonical || "";
    if (!canonical) return null;
    const url = new URL(canonical);
    const links = buildCanonical(url.origin, url.pathname + url.search);
    // react-router expects a single link definition or array
    return Array.isArray(links) ? links : [links];
  },
};
